import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CreateSupplierRequest,
  CreateSupplierRequestSchema,
  SUPPLIER_DOCUMENT_MAX_BYTES,
  SUPPLIER_DOCUMENT_MIME_TYPES,
  type SupplierDetail,
  type SupplierDocumentKind,
  type SupplierEditForm,
  SupplierEditFormSchema,
  type SupplierKind,
  type SupplierListItem,
  type SupplierRegion,
} from '@utazzo/shared';
import {
  UzButton,
  UzDialog,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { FileText, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';

import { downloadSupplierDocument, uploadSupplierDocument } from '../suppliers.api';
import {
  useCreateSupplier,
  useCurrencyOptions,
  useSupplierDetail,
  useUpdateSupplier,
} from '../use-suppliers';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { extractServerError } from '@/lib/api-error';

interface SupplierFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** The list row being edited; the popup loads the full record itself. */
  supplier?: SupplierDetail | SupplierListItem | null;
}

const REGION_OPTIONS: ReadonlyArray<{ value: SupplierRegion; label: string }> = [
  { value: 'DOMESTIC', label: 'Domestic (India)' },
  { value: 'INTERNATIONAL', label: 'International' },
];

const KIND_OPTIONS: ReadonlyArray<{ value: SupplierKind; label: string }> = [
  { value: 'DMC', label: 'DMC' },
  { value: 'HOTEL_AGGREGATOR', label: 'Hotel aggregator' },
  { value: 'AIRLINE_CONSOLIDATOR', label: 'Airline consolidator' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'ACTIVITY', label: 'Activity' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'VISA', label: 'Visa' },
  { value: 'OTHER', label: 'Other' },
];

type DocumentFieldName = 'gstinDocumentKey' | 'panDocumentKey' | 'bankProofDocumentKey';

/**
 * One proof document (GST certificate, PAN card, bank proof). The file goes to
 * S3 as soon as it is picked; the form field holds the resulting key, which is
 * stored when the supplier is saved. "View" works for the file already saved
 * on the supplier - a freshly picked one has no record to download from yet.
 */
const SupplierDocumentField = ({
  name,
  kind,
  label,
  hint,
  supplierId,
  savedKey,
  disabled,
  onUploadingChange,
}: {
  name: DocumentFieldName;
  kind: SupplierDocumentKind;
  label: string;
  hint?: string;
  supplierId: string | null;
  savedKey: string | null;
  disabled: boolean;
  onUploadingChange: (delta: 1 | -1) => void;
}): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  return (
    <UzFormField name={name}>
      {({ field, error }) => {
        const key = (field.value as string | null | undefined) ?? null;

        const upload = async (file: File): Promise<void> => {
          setUploadError(null);
          if (!(SUPPLIER_DOCUMENT_MIME_TYPES as readonly string[]).includes(file.type)) {
            setUploadError('Upload a PDF, JPG, PNG or WebP file.');
            return;
          }
          if (file.size > SUPPLIER_DOCUMENT_MAX_BYTES) {
            setUploadError('File is larger than 10 MB.');
            return;
          }
          setUploading(true);
          onUploadingChange(1);
          try {
            field.onChange(await uploadSupplierDocument(kind, file));
          } catch (err) {
            setUploadError(extractServerError(err).message ?? 'Upload failed. Try again.');
          } finally {
            setUploading(false);
            onUploadingChange(-1);
          }
        };

        const view = async (): Promise<void> => {
          if (!supplierId) return;
          try {
            const { presignedUrl } = await downloadSupplierDocument(supplierId, kind);
            window.open(presignedUrl, '_blank', 'noopener,noreferrer');
          } catch (err) {
            setUploadError(extractServerError(err).message ?? 'Could not open the file.');
          }
        };

        return (
          <div className="space-y-1.5">
            <p className="text-sm font-medium">{label}</p>
            <input
              ref={inputRef}
              type="file"
              hidden
              aria-label={label}
              accept={SUPPLIER_DOCUMENT_MIME_TYPES.join(',')}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void upload(file);
                e.target.value = '';
              }}
            />
            {key ? (
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <FileText className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {key.slice(key.lastIndexOf('/') + 1)}
                </span>
                {supplierId && key === savedKey && (
                  <UzButton size="sm" variant="ghost" onClick={() => void view()}>
                    View
                  </UzButton>
                )}
                <UzButton
                  size="sm"
                  variant="ghost"
                  onClick={() => inputRef.current?.click()}
                  loading={uploading}
                  disabled={disabled}
                >
                  Replace
                </UzButton>
                <UzButton
                  size="sm"
                  variant="ghost"
                  onClick={() => field.onChange(null)}
                  disabled={disabled || uploading}
                  aria-label={`Remove ${label}`}
                >
                  <X className="h-4 w-4 text-zinc-400" />
                </UzButton>
              </div>
            ) : (
              <UzButton
                size="sm"
                variant="outline"
                onClick={() => inputRef.current?.click()}
                loading={uploading}
                disabled={disabled}
              >
                <Upload className="mr-1.5 h-4 w-4" />
                Upload
              </UzButton>
            )}
            {(uploadError ?? error) ? (
              <p className="text-destructive text-xs">{uploadError ?? error}</p>
            ) : (
              hint && <p className="text-xs text-zinc-500">{hint}</p>
            )}
          </div>
        );
      }}
    </UzFormField>
  );
};

/**
 * The local bank routing code goes by the account's country; suppliers do
 * not store a country, so the account currency stands in for it.
 */
const ROUTING_LABEL: Record<string, { label: string; hint: string }> = {
  USD: { label: 'ABA routing number', hint: '9-digit US routing number' },
  GBP: { label: 'Sort code', hint: '6-digit UK sort code, e.g. 60-16-13' },
  AUD: { label: 'BSB number', hint: '6-digit Australian BSB' },
  CAD: { label: 'Transit / institution number', hint: '5-digit transit + 3-digit institution' },
  NZD: { label: 'Bank / branch code', hint: 'First 6 digits of the NZ account' },
  INR: { label: 'IFSC', hint: '11-character IFSC' },
};
const DEFAULT_ROUTING = {
  label: 'Routing code',
  hint: 'Local bank code if the country uses one (ABA, sort code, BSB...). Not needed with an IBAN.',
};

/** Optional text field that stores null for blank. */
const OptionalText = ({
  name,
  label,
  hint,
  placeholder,
  pending,
  upper,
  maxLength,
  multiline,
}: {
  name: string;
  label: string;
  hint?: string;
  placeholder?: string;
  pending: boolean;
  upper?: boolean;
  maxLength?: number;
  multiline?: boolean;
}): React.JSX.Element => (
  <UzFormField name={name}>
    {({ field, error }) => {
      const props = {
        ...field,
        value: (field.value as string | null | undefined) ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          field.onChange(
            e.target.value === '' ? null : upper ? e.target.value.toUpperCase() : e.target.value,
          ),
        label,
        hint,
        placeholder,
        error,
        disabled: pending,
      };
      return multiline ? (
        <UzTextarea {...props} rows={2} />
      ) : (
        <UzInput {...props} maxLength={maxLength} />
      );
    }}
  </UzFormField>
);

/** Bank details for paying an international supplier (all optional). */
const InternationalBankSection = ({
  pending,
  currencySelectOptions,
  currenciesLoading,
}: {
  pending: boolean;
  currencySelectOptions: ReadonlyArray<{ value: string; label: string }>;
  currenciesLoading: boolean;
}): React.JSX.Element => {
  const currencyId = useWatch({ name: 'bankAccountCurrencyId' }) as string | null | undefined;
  const code = currencySelectOptions.find((o) => o.value === currencyId)?.label.split(' ')[0];
  const routing = (code && ROUTING_LABEL[code]) || DEFAULT_ROUTING;
  return (
    <fieldset className="grid gap-4 rounded-lg border border-zinc-200 p-4 md:col-span-2 md:grid-cols-2">
      <legend className="px-1 text-sm font-semibold text-zinc-800">Bank (international)</legend>
      <OptionalText
        name="bankBeneficiaryName"
        label="Beneficiary name"
        hint="Exactly as on the bank account"
        pending={pending}
      />
      <OptionalText
        name="bankAccountNumber"
        label="Account number / IBAN"
        hint="IBAN for Europe, UK, Middle East; else the account number"
        pending={pending}
        upper
        maxLength={40}
      />
      <UzFormField name="swiftCode">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) =>
              field.onChange(e.target.value === '' ? null : e.target.value.toUpperCase())
            }
            label="SWIFT code"
            hint="8 or 11 character bank SWIFT / BIC"
            maxLength={11}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <OptionalText
        name="bankRoutingCode"
        label={routing.label}
        hint={routing.hint}
        pending={pending}
        upper
        maxLength={40}
      />
      <OptionalText
        name="bankIntermediarySwift"
        label="Intermediary bank SWIFT"
        hint="Correspondent bank, if theirs asks for one (often for USD)"
        pending={pending}
        upper
        maxLength={11}
      />
      <UzFormField name="bankAccountCurrencyId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Account currency"
            hint="Currency the account is held in - what we send"
            placeholder={currenciesLoading ? 'Loading currencies…' : 'Select a currency'}
            options={currencySelectOptions}
            value={(field.value as string | null | undefined) || undefined}
            onValueChange={(v) => field.onChange(v || null)}
            error={error}
            disabled={pending || currenciesLoading}
          />
        )}
      </UzFormField>
      <div className="md:col-span-2">
        <OptionalText
          name="bankAddress"
          label="Bank address"
          placeholder="Branch, street, city, country"
          pending={pending}
          multiline
        />
      </div>
    </fieldset>
  );
};

/** Account numbers compare without spaces / case. */
const normAccount = (v: string | null | undefined): string =>
  (v ?? '').replace(/\s+/gu, '').toUpperCase();

const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/u;
const ACCOUNT_TYPE_OPTIONS = [
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CURRENT', label: 'Current' },
];

/**
 * Bank details for paying a domestic supplier (all optional). Typing a full
 * IFSC looks it up on Razorpay's free IFSC API (CORS-open) and fills bank
 * name + branch; both stay editable.
 */
const DomesticBankSection = ({
  pending,
  accountConfirm,
  onAccountConfirmChange,
  accountConfirmError,
}: {
  pending: boolean;
  accountConfirm: string;
  onAccountConfirmChange: (v: string) => void;
  accountConfirmError: string | null;
}): React.JSX.Element => {
  const { setValue } = useFormContext();
  const [ifscNote, setIfscNote] = useState<string | null>(null);
  const lookupRef = useRef<AbortController | null>(null);

  const lookupIfsc = (ifsc: string): void => {
    lookupRef.current?.abort();
    if (!IFSC_RE.test(ifsc)) {
      setIfscNote(null);
      return;
    }
    const ctrl = new AbortController();
    lookupRef.current = ctrl;
    setIfscNote('Looking up IFSC…');
    fetch(`https://ifsc.razorpay.com/${ifsc}`, { signal: ctrl.signal })
      .then(async (res) => {
        if (res.status === 404) {
          setIfscNote('IFSC not found - check it, or fill bank and branch by hand.');
          return;
        }
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { BANK?: string; BRANCH?: string; CITY?: string };
        if (data.BANK) setValue('bankName', data.BANK, { shouldDirty: true });
        if (data.BRANCH) setValue('bankBranch', data.BRANCH, { shouldDirty: true });
        setIfscNote(
          `${data.BANK ?? ''}${data.BRANCH ? ` - ${data.BRANCH}` : ''}${data.CITY ? `, ${data.CITY}` : ''}`,
        );
      })
      .catch((err: unknown) => {
        if ((err as { name?: string }).name === 'AbortError') return;
        setIfscNote('Could not look up the IFSC - fill bank and branch by hand.');
      });
  };

  return (
    <fieldset className="grid gap-4 rounded-lg border border-zinc-200 p-4 md:col-span-2 md:grid-cols-2">
      <legend className="px-1 text-sm font-semibold text-zinc-800">Bank (domestic)</legend>
      <div className="md:col-span-2">
        <OptionalText
          name="bankBeneficiaryName"
          label="Account holder name"
          hint="Exactly as on the bank account"
          pending={pending}
        />
      </div>
      <OptionalText
        name="bankAccountNumber"
        label="Account number"
        pending={pending}
        maxLength={40}
      />
      <UzInput
        label="Confirm account number"
        value={accountConfirm}
        onChange={(e) => onAccountConfirmChange(e.target.value)}
        onPaste={(e) => e.preventDefault()}
        hint="Type it again (pasting is off) to catch typos"
        maxLength={40}
        error={accountConfirmError ?? undefined}
        disabled={pending}
        autoComplete="off"
      />
      <UzFormField name="bankIfsc">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => {
              const v = e.target.value.toUpperCase().trim();
              field.onChange(v === '' ? null : v);
              lookupIfsc(v);
            }}
            label="IFSC"
            hint={ifscNote ?? '11 characters, e.g. HDFC0000001 - fills bank and branch'}
            maxLength={11}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="bankAccountType">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Account type"
            placeholder="Savings or current"
            options={ACCOUNT_TYPE_OPTIONS}
            value={(field.value as string | null | undefined) || undefined}
            onValueChange={(v) => field.onChange(v || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <OptionalText name="bankName" label="Bank name" pending={pending} />
      <OptionalText name="bankBranch" label="Branch" pending={pending} />
      <OptionalText
        name="bankUpiId"
        label="UPI ID"
        hint="For small or quick payments, e.g. name@okhdfc"
        pending={pending}
      />
    </fieldset>
  );
};

const CREATE_DEFAULTS: CreateSupplierRequest = {
  code: '',
  name: '',
  kind: 'DMC',
  isActive: true,
  sortOrder: 0,
};

/**
 * Field set shared by create + edit. Reads the form via context
 * (UzFormField → useFormContext), so it renders inside either UzForm.
 *
 * The currency dropdown is server-driven from GET /currencies/options. It is
 * optional (no currency = null); the resolved label is shown in the list page.
 *
 * Region decides what is mandatory: GSTIN + PAN for a domestic supplier
 * (enforced by the shared schema's refinement).
 */
const SupplierFields = ({
  pending,
  currencySelectOptions,
  currenciesLoading,
  supplier,
  onUploadingChange,
  accountConfirm,
  onAccountConfirmChange,
  accountConfirmError,
}: {
  pending: boolean;
  accountConfirm: string;
  onAccountConfirmChange: (v: string) => void;
  accountConfirmError: string | null;
  currencySelectOptions: ReadonlyArray<{ value: string; label: string }>;
  currenciesLoading: boolean;
  /** The saved record in edit mode - its stored documents can be viewed. */
  supplier: SupplierDetail | null;
  onUploadingChange: (delta: 1 | -1) => void;
}): React.JSX.Element => {
  const region = useWatch({ name: 'region' }) as SupplierRegion | null | undefined;
  const documentProps = { supplierId: supplier?.id ?? null, disabled: pending, onUploadingChange };

  return (
    <>
      <UzFormField name="name">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Name"
            placeholder="e.g. Intrepid Travel"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="kind">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Kind"
            required
            placeholder="Select a kind"
            options={KIND_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
            value={(field.value as string) || undefined}
            onValueChange={field.onChange}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="region">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Supplier type"
            hint="Domestic needs GSTIN and PAN."
            required
            placeholder="Domestic or international"
            options={REGION_OPTIONS as ReadonlyArray<{ value: string; label: string }>}
            value={(field.value as string | null | undefined) || undefined}
            onValueChange={field.onChange}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="defaultCurrencyId">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Default currency"
            hint="Currency the supplier invoices in. Leave blank if none."
            placeholder={currenciesLoading ? 'Loading currencies…' : 'Select a currency'}
            options={currencySelectOptions}
            value={(field.value as string | null | undefined) || undefined}
            onValueChange={(v) => field.onChange(v || null)}
            error={error}
            disabled={pending || currenciesLoading}
          />
        )}
      </UzFormField>
      <UzFormField name="contactName">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Contact name"
            placeholder="e.g. Jane Smith"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="contactEmail">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            // Empty maps to null so the optional email validates when blank
            // (the schema rejects '' against the email() rule).
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Contact email"
            placeholder="e.g. jane@supplier.com"
            type="email"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="contactPhone">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Contact phone"
            placeholder="e.g. +91 98765 43210"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="contractEmail">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Contract email"
            hint="Where contracts and agreements are sent, if different from the contact."
            placeholder="e.g. contracts@supplier.com"
            type="email"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="creditDays">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Credit days"
            hint="Net credit days (e.g. 7, 15, 30)."
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="md:col-span-2">
        <UzFormField name="address">
          {({ field, error }) => (
            <UzTextarea
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="Address"
              placeholder="Street, city, state, country"
              rows={2}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="website">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Website"
            placeholder="https://supplier.com"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-4 md:col-span-2 md:grid-cols-2">
        <UzFormField name="taxIdGstin">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? null : e.target.value.toUpperCase())
              }
              label="GSTIN"
              hint="15-char Indian GST registration"
              maxLength={15}
              error={error}
              required={region === 'DOMESTIC'}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="taxIdPan">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? null : e.target.value.toUpperCase())
              }
              label="PAN"
              hint="10-char Indian PAN"
              maxLength={10}
              error={error}
              required={region === 'DOMESTIC'}
              disabled={pending}
            />
          )}
        </UzFormField>
        <SupplierDocumentField
          name="gstinDocumentKey"
          kind="GSTIN"
          label="GST certificate"
          savedKey={supplier?.gstinDocumentKey ?? null}
          {...documentProps}
        />
        <SupplierDocumentField
          name="panDocumentKey"
          kind="PAN"
          label="PAN card"
          savedKey={supplier?.panDocumentKey ?? null}
          {...documentProps}
        />
      </div>
      {/* Bank details: international (with SWIFT) or domestic (IFSC), by supplier type. */}
      {region === 'INTERNATIONAL' ? (
        <InternationalBankSection
          pending={pending}
          currencySelectOptions={currencySelectOptions}
          currenciesLoading={currenciesLoading}
        />
      ) : region === 'DOMESTIC' ? (
        <DomesticBankSection
          pending={pending}
          accountConfirm={accountConfirm}
          onAccountConfirmChange={onAccountConfirmChange}
          accountConfirmError={accountConfirmError}
        />
      ) : null}
      <div className="md:col-span-2">
        <SupplierDocumentField
          name="bankProofDocumentKey"
          kind="BANK_PROOF"
          label="Cancelled cheque / bank statement"
          hint="PDF, JPG, PNG or WebP, up to 10 MB."
          savedKey={supplier?.bankProofDocumentKey ?? null}
          {...documentProps}
        />
      </div>
      <UzFormField name="externalApiCredentialKey">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="External API credential key"
            hint="Secrets Manager key for aggregators (e.g. 'tbo.hotel.username'). Leave blank if not an aggregator."
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="md:col-span-2">
        <UzFormField name="notes">
          {({ field, error }) => (
            <UzTextarea
              {...field}
              value={(field.value as string | undefined) ?? ''}
              label="Notes"
              placeholder="Optional internal notes"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="sortOrder">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Sort order"
            precision={0}
            value={field.value === null || field.value === undefined ? '' : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 0)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field }) => (
          <UzSwitch
            label="Active"
            description="Inactive suppliers are hidden from booking forms."
            checked={Boolean(field.value)}
            onCheckedChange={field.onChange}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

/** Two columns in the popup; long fields span both (md:col-span-2). */
const FORM_GRID = 'grid gap-4 space-y-0 md:grid-cols-2';

export const SupplierFormDrawer = ({
  open,
  onOpenChange,
  mode,
  supplier,
}: SupplierFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const currenciesQuery = useCurrencyOptions();
  // The list row lacks address, tax ids and documents - edit the full record.
  const detailQuery = useSupplierDetail(!isCreate && open && supplier ? supplier.id : null);
  const detail = isCreate ? null : (detailQuery.data ?? null);
  const [uploadsInFlight, setUploadsInFlight] = useState(0);
  // "Confirm account number" (domestic) - form-only, never sent.
  const [accountConfirm, setAccountConfirm] = useState('');
  const [accountConfirmError, setAccountConfirmError] = useState<string | null>(null);
  // Which supplier the edit form currently holds, so a background refetch
  // does not overwrite what the user has typed.
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const pending =
    createSupplier.isPending ||
    updateSupplier.isPending ||
    (!isCreate && loadedId !== supplier?.id);

  const currencySelectOptions = useMemo(
    () =>
      (currenciesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: c.symbol ? `${c.code} (${c.symbol})` : c.code,
      })),
    [currenciesQuery.data],
  );

  const createForm = useForm<CreateSupplierRequest>({
    resolver: zodResolver(CreateSupplierRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<SupplierEditForm>({
    resolver: zodResolver(SupplierEditFormSchema),
    defaultValues: {},
  });

  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) {
      setLoadedId(null);
      return;
    }
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
      setAccountConfirm('');
      setAccountConfirmError(null);
    } else if (detail && !detailQuery.isFetching && loadedId !== detail.id) {
      setLoadedId(detail.id);
      // The saved number counts as confirmed; changing it asks again.
      setAccountConfirm(detail.bankAccountNumber ?? '');
      setAccountConfirmError(null);
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: detail.name,
        kind: detail.kind,
        region: detail.region,
        contactName: detail.contactName ?? '',
        contactEmail: detail.contactEmail ?? null,
        contactPhone: detail.contactPhone ?? '',
        contractEmail: detail.contractEmail ?? null,
        defaultCurrencyId: detail.defaultCurrencyId ?? null,
        creditDays: detail.creditDays ?? null,
        notes: detail.notes ?? '',
        address: detail.address ?? null,
        website: detail.website ?? null,
        taxIdGstin: detail.taxIdGstin ?? null,
        taxIdPan: detail.taxIdPan ?? null,
        swiftCode: detail.swiftCode ?? null,
        gstinDocumentKey: detail.gstinDocumentKey ?? null,
        panDocumentKey: detail.panDocumentKey ?? null,
        bankProofDocumentKey: detail.bankProofDocumentKey ?? null,
        bankBeneficiaryName: detail.bankBeneficiaryName ?? null,
        bankAccountNumber: detail.bankAccountNumber ?? null,
        bankRoutingCode: detail.bankRoutingCode ?? null,
        bankIntermediarySwift: detail.bankIntermediarySwift ?? null,
        bankAccountCurrencyId: detail.bankAccountCurrencyId ?? null,
        bankAddress: detail.bankAddress ?? null,
        bankIfsc: detail.bankIfsc ?? null,
        bankName: detail.bankName ?? null,
        bankBranch: detail.bankBranch ?? null,
        bankAccountType: detail.bankAccountType ?? null,
        bankUpiId: detail.bankUpiId ?? null,
        externalApiCredentialKey: detail.externalApiCredentialKey ?? null,
        sortOrder: detail.sortOrder,
        isActive: detail.isActive,
      });
    }
  }, [open, isCreate, detail, detailQuery.isFetching, loadedId, createForm, updateForm]);

  const toNullIfEmpty = (v: string | null | undefined): string | null =>
    v?.trim() ? v.trim() : null;

  /** Domestic account number typed twice must match (blank skips the check). */
  const accountConfirmed = (values: {
    region?: string | null;
    bankAccountNumber?: string | null;
  }): boolean => {
    const number = normAccount(values.bankAccountNumber);
    if (values.region !== 'DOMESTIC' || !number || normAccount(accountConfirm) === number) {
      setAccountConfirmError(null);
      return true;
    }
    setAccountConfirmError('Account numbers do not match');
    return false;
  };

  const onCreate = createForm.handleSubmit(async (values) => {
    if (!accountConfirmed(values)) return;
    // `code` is derived from the name (hidden field).
    await createSupplier.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      contactName: toNullIfEmpty(values.contactName),
      contactEmail: toNullIfEmpty(values.contactEmail),
      contactPhone: toNullIfEmpty(values.contactPhone),
      notes: toNullIfEmpty(values.notes),
      defaultCurrencyId: values.defaultCurrencyId || null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!supplier || !accountConfirmed(values)) return;
    await updateSupplier.mutateAsync({
      id: supplier.id,
      payload: {
        ...values,
        contactName: toNullIfEmpty(values.contactName),
        contactEmail: toNullIfEmpty(values.contactEmail),
        contactPhone: toNullIfEmpty(values.contactPhone),
        notes: toNullIfEmpty(values.notes),
        defaultCurrencyId: values.defaultCurrencyId || null,
      },
    });
    onOpenChange(false);
  });

  const fields = (
    <SupplierFields
      pending={pending}
      currencySelectOptions={currencySelectOptions}
      currenciesLoading={currenciesQuery.isLoading}
      supplier={detail}
      onUploadingChange={(delta) => setUploadsInFlight((n) => n + delta)}
      accountConfirm={accountConfirm}
      onAccountConfirmChange={(v) => {
        setAccountConfirm(v);
        setAccountConfirmError(null);
      }}
      accountConfirmError={accountConfirmError}
    />
  );

  return (
    <UzDialog
      open={open}
      onOpenChange={onOpenChange}
      size="2xl"
      title={isCreate ? 'Add supplier' : `Edit ${supplier?.name ?? 'supplier'}`}
      description="The name is what appears in bookings."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton
            onClick={isCreate ? onCreate : onUpdate}
            loading={createSupplier.isPending || updateSupplier.isPending}
            disabled={pending || uploadsInFlight > 0}
          >
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate} className={FORM_GRID}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate} className={FORM_GRID}>
          {detailQuery.isError && (
            <p className="text-destructive text-sm md:col-span-2">
              Could not load this supplier. Close the popup and try again.
            </p>
          )}
          {fields}
        </UzForm>
      )}
    </UzDialog>
  );
};
